import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useOpenBacklogConnect } from '@/components/backlogConnectContext'
import { fmt } from '@/constants/ui-locale'
import { projectFullLabel, projectOptionLabel } from '@/utils/projectOptionLabel'
import { useUiStrings } from '@/hooks/useUiStrings'
import type { ProjectUpdateStatus } from '@/hooks/useProxySttRecording'
import { ExclamationTriangleIcon, XMarkIcon } from '@/components/Icons'
import type { CopilotSetupNeed } from './copilotSetupState'
import CopilotPillSelect from './CopilotPillSelect'
import { useCopilotBacklogSetup } from './useCopilotBacklogSetup'

/** How long a "that worked" line stays up before it clears itself. */
const CONFIRMATION_MS = 6000

/**
 * Shared so the loading state, the settled one, and the model row above them
 * are all the same box.
 */
export const STRIP_CLASS =
  'border-b border-[#dbe3f7] bg-[#e8edfc] px-3 py-2.5 dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)]'

/**
 * What became of a project the reader picked here.
 *
 * `saved` and `partial` are both "the meeting row has it, this recording does
 * not" — kept apart because only one of them is worth a warning. `saved` is a
 * meeting that is not running, where there is no session to miss it.
 */
export type ChooseProjectResult = 'applied' | 'saved' | 'failed'

type Outcome =
  /**
   * `seenPending` is what keeps an older answer from settling this one.
   *
   * The status prop is shared with every other `project.update` the meeting
   * sends — the Backlog account goes out on its own as the panel opens — so by
   * the time a project is picked it usually already reads `success`, about a
   * frame nobody is waiting for. Waiting to see this request turn the status
   * to `pending` is what makes the next answer this request's.
   */
  | { kind: 'applying'; project: string; seenPending: boolean }
  | { kind: 'done'; project: string }
  | { kind: 'partial'; project: string }
  | { kind: 'failed' }

interface CopilotSetupStripProps {
  /** The project on the meeting row, or null/'' for none. */
  meetingProjectKey: string | null
  /** False on a meeting the live update cannot be used on, and while shut. */
  enabled: boolean
  /**
   * Write the project to the meeting, then apply it to the running session.
   *
   * @returns `applied` when a live session was told and an acknowledgment is
   *   expected, `saved` when the meeting took it but nothing is running, and
   *   `failed` when the meeting itself could not be saved.
   */
  onChooseProject: (projectKey: string) => Promise<ChooseProjectResult>
  /**
   * The Copilot model dropdown, to stand beside the project one.
   *
   * A slot rather than this component's own concern: the model has nothing to
   * do with Backlog, and its own rules for when to appear. What they share is
   * the row, which is the only reason it is passed through here at all —
   * two controls side by side cannot be two sibling components, because each
   * of them disappears on its own terms.
   */
  modelControl?: ReactNode
  /**
   * The Web search / Backlog chips, to open the same row these pickers sit on.
   *
   * Passed through rather than rendered as a sibling above, which is what it
   * was: the four controls wrap as one group in the design, and two sibling
   * rows cannot share a line. Nothing here reads it — it is placed, not used.
   */
  routesControl?: ReactNode
  /**
   * Whether to offer the Backlog invitations at all.
   *
   * False once the reader turns the Backlog route off: they have said they do
   * not want ticket answers, and an invitation to set up what they just
   * declined is the panel arguing with them. The project picker is unaffected
   * — a typed question about a project is still answered, and the minutes use
   * it either way.
   */
  offersBacklogSetup?: boolean
  /** Report a working Backlog account up to whoever owns the session. */
  onAccountKnown: (accountId: number) => void
  /** How the running session took the last change this strip asked for. */
  projectUpdateStatus: ProjectUpdateStatus
  /** True while merged into a BrSE shared room (room-wide project rules apply). */
  inSharedRoom?: boolean
  /** Project key the room chose, from `sharedroom.members.roomProject`. */
  roomProjectKey?: string | null
  /** F2 notice when the meeting row still differs from the room project. */
  roomProjectMismatchNotice?: string | null
}

/**
 * The Copilot's Backlog controls: which project it answers about, and the way
 * in when there is no account behind it yet (BRAI-689).
 *
 * Both used to surface only as a refusal — the reader typed a question,
 * waited, and got back a sentence naming something to go and fix elsewhere,
 * and elsewhere was a second tab after which this panel went on believing
 * nothing had changed. Both are knowable when the panel opens, so they are
 * read then, and both are answered from here.
 *
 * The picker stays put once a project is chosen. It is a control, not a
 * prompt: a meeting can turn out to be about a different project than the one
 * it was booked for, and a control that vanishes the moment it is used can
 * only ever be used once. The invitations around it — connect, reconnect, no
 * projects at all — are notices, so those can be dismissed and the picker
 * cannot.
 */
export default function CopilotSetupStrip({
  meetingProjectKey,
  enabled,
  onChooseProject,
  onAccountKnown,
  projectUpdateStatus,
  modelControl = null,
  routesControl = null,
  offersBacklogSetup = true,
  inSharedRoom = false,
  roomProjectKey = null,
  roomProjectMismatchNotice = null,
}: CopilotSetupStripProps) {
  const { t } = useUiStrings()
  const [dismissed, setDismissed] = useState<CopilotSetupNeed | null>(null)
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const openConnect = useOpenBacklogConnect()
  const { need, projects, loading, refresh } = useCopilotBacklogSetup({ enabled, onAccountKnown })

  // The session's answer arrives on a prop rather than from the call, because
  // it is an acknowledgment on the transcript socket and not a reply to
  // anything this component sent.
  useEffect(() => {
    if (outcome?.kind !== 'applying') return
    if (projectUpdateStatus === 'pending') {
      if (!outcome.seenPending) setOutcome({ ...outcome, seenPending: true })
      return
    }
    // Anything still on the prop before that belongs to an earlier frame.
    if (!outcome.seenPending) return
    if (projectUpdateStatus === 'success') setOutcome({ kind: 'done', project: outcome.project })
    if (projectUpdateStatus === 'error' || projectUpdateStatus === 'unsupported') {
      setOutcome({ kind: 'partial', project: outcome.project })
    }
  }, [outcome, projectUpdateStatus])

  // The confirmation clears itself. The picker it sits above is permanent, and
  // a line reading "Copilot is now using BRAI" an hour later says nothing.
  useEffect(() => {
    if (outcome?.kind !== 'done') return undefined
    const timer = window.setTimeout(() => setOutcome(null), CONFIRMATION_MS)
    return () => window.clearTimeout(timer)
  }, [outcome])

  async function choose(projectKey: string) {
    if (!projectKey || projectKey === meetingProjectKey) return
    setOutcome({ kind: 'applying', project: projectKey, seenPending: false })
    const result = await onChooseProject(projectKey)
    if (result === 'failed') return setOutcome({ kind: 'failed' })
    // Read again: a grant that stopped working between the panel opening and
    // this press is worth finding out about now rather than on the next
    // question, and this is the moment the reader is looking at the strip.
    refresh()
    if (result === 'saved') setOutcome({ kind: 'done', project: projectKey })
  }

  const showPicker = need === 'pick-project'
  const isNotice = need === 'connect' || need === 'reauth' || need === 'no-projects'
  const showNotice = isNotice && need !== dismissed && offersBacklogSetup
  const roomKey = roomProjectKey?.trim() ?? ''
  const hasProject = Boolean(meetingProjectKey?.trim())
  const copilotProjectKey = inSharedRoom && roomKey ? roomKey : meetingProjectKey?.trim() ?? ''
  const chosenProject = projects.find((project) => project.projectKey === copilotProjectKey)
  const roomKeyMissingFromGrant =
    inSharedRoom && roomKey.length > 0 && !projects.some((project) => project.projectKey === roomKey)
  const selectValue = inSharedRoom && roomKey ? roomKey : (meetingProjectKey ?? '')
  // A value with no row behind it leaves the control blank and looking broken.
  // That happens for a meeting with no project, and for one whose project this
  // reader's own grant cannot see.
  const needsPlaceholder = chosenProject === undefined && !roomKeyMissingFromGrant

  if (!enabled && !modelControl && !routesControl) return null

  /** What the panel searches, on the left of it; what it is set to, on the right. */
  const divider = (
    <span
      aria-hidden="true"
      data-testid="copilot-filter-divider"
      className="h-4 w-px shrink-0 bg-[#cbd5e1] dark:bg-[var(--border-color)]"
    />
  )

  /** The chips and the pickers, wrapping as one group. */
  const pillRow = (
    <div className="flex flex-wrap items-center gap-1.5">
      {routesControl}
      {routesControl && (showPicker || modelControl) ? divider : null}
      {showPicker ? (
        <CopilotPillSelect
          label={t.projectLabel}
          // The key alone on the chip; the options carry key and name, which
          // is what there is room for in a dropdown and not in a 120px pill.
          display={
            copilotProjectKey
              ? inSharedRoom && roomKey
                ? fmt(t.sharedRoomProjectRoomLabel, { project: copilotProjectKey })
                : copilotProjectKey
              : t.projectPlaceholder
          }
          testId="copilot-setup-project-select"
          // Warm until a project is settled — the same amber as the line
          // below it, so the warning and the control it names read as one
          // thing rather than as a notice next to an unrelated dropdown.
          tone={copilotProjectKey ? 'neutral' : 'attention'}
          icon={
            copilotProjectKey ? null : (
              <span
                aria-hidden="true"
                className="h-2 w-2 shrink-0 rounded-full bg-[#f59e0b]"
              />
            )
          }
          value={selectValue}
          title={chosenProject && projectFullLabel(chosenProject.projectKey, chosenProject.name)}
          onChange={(projectKey) => void choose(projectKey)}
        >
          {needsPlaceholder && !inSharedRoom ? (
            <option value="">{t.projectPlaceholder}</option>
          ) : null}
          {roomKeyMissingFromGrant ? (
            <option value={roomKey} title={roomKey}>
              {roomKey}
            </option>
          ) : null}
          {projects.map((project) => (
            <option
              key={project.projectKey}
              value={project.projectKey}
              title={projectFullLabel(project.projectKey, project.name)}
            >
              {projectOptionLabel(project.projectKey, project.name)}
            </option>
          ))}
        </CopilotPillSelect>
      ) : null}
      {modelControl}
    </div>
  )

  // The control arrives disabled rather than the strip arriving late. Both
  // reads are a round trip to Backlog, and the panel used to open on nothing
  // and then grow a control under whatever the reader had started reading.
  //
  // It guesses the picker, which is what the great majority of readers get. A
  // reader with no grant sees it become the invitation to connect instead —
  // a swap inside a box that was already there, rather than a box appearing.
  if (enabled && loading) {
    return (
      <div className={STRIP_CLASS} data-testid="copilot-setup-strip">
        <div className="flex flex-wrap items-center gap-1.5">
          {routesControl}
          {routesControl ? divider : null}
          <CopilotPillSelect
            label={t.projectLabel}
            display={t.projectLoading}
            testId="copilot-setup-loading"
            value=""
            onChange={() => undefined}
            disabled
          >
            <option value="">{t.projectLoading}</option>
          </CopilotPillSelect>
          {modelControl}
        </div>
      </div>
    )
  }

  if (!showPicker && !showNotice && outcome === null && !modelControl && !routesControl) {
    return null
  }

  return (
    <div className={STRIP_CLASS} data-testid="copilot-setup-strip">
      {pillRow}

      {/* Under the row, not inside it: the warning is about one of the chips
          in that row, and a sentence wrapped between two controls would read
          as a third one. Amber, matching the picker it names — the two are
          one thing said twice, once as a state and once as a sentence. */}
      {roomProjectMismatchNotice ? (
        <p
          role="status"
          data-testid="copilot-setup-room-project-notice"
          className="mt-2 flex items-start gap-2 border-t border-[#fbeda8] bg-[#f9f7d4] px-3 py-1.5 text-xs leading-[18px] text-[#78350f] dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
        >
          <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1">{roomProjectMismatchNotice}</span>
        </p>
      ) : null}

      {showPicker && !hasProject && !inSharedRoom ? (
        <p
          role="status"
          data-testid="copilot-setup-no-project"
          // `py-1.5` and `leading-[18px]`: the design's band is 48px around two
          // lines, which is 6px of padding either side of them and not 8.
          className="mt-2 flex items-start gap-2 border-t border-[#fbeda8] bg-[#f9f7d4] px-3 py-1.5 text-xs leading-[18px] text-[#78350f] dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
        >
          <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1">{t.copilotSetupProjectWarning}</span>
        </p>
      ) : null}

      <div className="mt-2 flex items-start gap-2 empty:mt-0">
        <div className="min-w-0 flex-1 space-y-1.5 empty:hidden">
          {outcome?.kind === 'applying' ? (
            <p className="text-xs text-[var(--text-secondary)]" role="status">
              {t.copilotSetupApplying}
            </p>
          ) : null}

          {outcome?.kind === 'done' ? (
            <p
              className="text-xs font-medium text-emerald-700 dark:text-emerald-400"
              role="status"
              data-testid="copilot-setup-applied"
            >
              {fmt(t.copilotSetupApplied, { project: outcome.project })}
            </p>
          ) : null}

          {outcome?.kind === 'partial' ? (
            <p
              className="text-xs text-amber-700 dark:text-amber-400"
              role="alert"
              data-testid="copilot-setup-session-missed"
            >
              {t.copilotSetupUnsupported}
            </p>
          ) : null}

          {outcome?.kind === 'failed' ? (
            <p
              className="text-xs text-red-600 dark:text-red-400"
              role="alert"
              data-testid="copilot-setup-save-failed"
            >
              {t.copilotSetupApplyFailed}
            </p>
          ) : null}

          {showNotice && need === 'no-projects' ? (
            <p className="text-xs text-[var(--text-secondary)]">{t.assistBacklogNoProjects}</p>
          ) : null}

          {showNotice && (need === 'connect' || need === 'reauth') ? (
            <>
              <p className="text-xs font-semibold text-[var(--text-primary)]">
                {need === 'reauth' ? t.copilotSetupReauthTitle : t.copilotSetupConnectTitle}
              </p>
              <p className="text-xs text-[var(--text-secondary)]">
                {need === 'reauth' ? t.copilotSetupReauthBody : t.copilotSetupConnectBody}
              </p>
              <button
                type="button"
                data-testid="copilot-setup-connect"
                onClick={() => openConnect?.()}
                disabled={openConnect === null}
                className="mt-1 inline-flex items-center rounded-md bg-[var(--accent)] px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-[var(--accent-hover)]"
              >
                {need === 'reauth' ? t.copilotSetupReauthAction : t.copilotSetupConnectAction}
              </button>
            </>
          ) : null}

        </div>

        {/* Notices can be waved away; the picker cannot, because dismissing a
            control is the same as taking it away. */}
        {showNotice ? (
          <button
            type="button"
            onClick={() => {
              setOutcome(null)
              setDismissed(need)
            }}
            aria-label={t.copilotSetupDismiss}
            title={t.copilotSetupDismiss}
            data-testid="copilot-setup-dismiss"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-secondary)]"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
    </div>
  )
}
